import { APP_NAME, APP_SUBTITLE, DEVELOPER_NAME } from '@idle-depths/shared';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle, Cloud, Sparkles } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';

import { LoginButton } from '@/components/auth/LoginButton';
import { Button, Card, CardContent, Spinner } from '@/components/ui';
import { cn } from '@/lib/utils';
import { useAppStore, useAuthStore } from '@/stores';

// Animation variants for staggered children
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 300,
      damping: 25,
    },
  },
};

const logoVariants = {
  hidden: { opacity: 0, scale: 0.8, y: -20 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 200,
      damping: 20,
      delay: 0.1,
    },
  },
};

const titleVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 200,
      damping: 25,
      delay: 0.3,
    },
  },
};

const subtitleVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring' as const,
      stiffness: 200,
      damping: 25,
      delay: 0.5,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring' as const,
      stiffness: 200,
      damping: 25,
      delay: 0.6,
    },
  },
};

const footerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delay: 0.9,
      duration: 0.5,
    },
  },
};

// Glow pulse animation for logo
const glowVariants = {
  initial: { opacity: 0.2, scale: 1 },
  animate: {
    opacity: [0.2, 0.4, 0.2],
    scale: [1, 1.1, 1],
    transition: {
      duration: 3,
      repeat: Infinity,
      ease: 'easeInOut' as const,
    },
  },
};

/**
 * Abyssal Beacon logo component - the game's main logo.
 * Features a glowing crystal/beacon descending into depth rings.
 */
function AbyssalBeaconLogo(): React.ReactElement {
  return (
    <motion.div
      className="relative h-28 w-28"
      variants={logoVariants}
      initial="hidden"
      animate="visible"
      whileHover={{ scale: 1.05 }}
      transition={{ type: 'spring' as const, stiffness: 400, damping: 17 }}
    >
      {/* Outer glow effect */}
      <motion.div
        className="absolute inset-0 rounded-full bg-biolume-cyan/20 blur-xl"
        variants={glowVariants}
        initial="initial"
        animate="animate"
      />

      {/* Main logo container */}
      <div className="relative h-full w-full rounded-full bg-gradient-to-b from-abyss-dark to-abyss-void p-1 shadow-[0_0_30px_rgba(34,211,238,0.3)]">
        <div className="flex h-full w-full items-center justify-center rounded-full bg-gradient-to-b from-abyss-mid to-abyss-void border border-biolume-cyan/20">
          {/* Depth rings */}
          <motion.div
            className="absolute inset-4 rounded-full border border-biolume-cyan/10"
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute inset-6 rounded-full border border-biolume-cyan/15"
            animate={{ rotate: -360 }}
            transition={{ duration: 45, repeat: Infinity, ease: 'linear' }}
          />
          <motion.div
            className="absolute inset-8 rounded-full border border-biolume-cyan/20"
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
          />

          {/* Crystal/Beacon SVG */}
          <motion.svg
            className="h-16 w-16 drop-shadow-[0_0_10px_rgba(34,211,238,0.5)]"
            viewBox="0 0 24 24"
            fill="none"
            animate={{ y: [0, -2, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          >
            <defs>
              <linearGradient id="crystalGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#67e8f9" />
                <stop offset="50%" stopColor="#22d3ee" />
                <stop offset="100%" stopColor="#0891b2" />
              </linearGradient>
              <radialGradient id="coreGlow" cx="50%" cy="35%" r="50%">
                <stop offset="0%" stopColor="#ffffff" />
                <stop offset="50%" stopColor="#67e8f9" />
                <stop offset="100%" stopColor="#22d3ee" stopOpacity="0" />
              </radialGradient>
            </defs>
            {/* Main crystal shape */}
            <path d="M12 2L15 9L12 22L9 9L12 2Z" fill="url(#crystalGrad)" />
            {/* Glowing core */}
            <ellipse cx="12" cy="9" rx="2.5" ry="3" fill="url(#coreGlow)" />
            <ellipse cx="12" cy="9" rx="1" ry="1.5" fill="white" opacity="0.9" />
          </motion.svg>
        </div>
      </div>
    </motion.div>
  );
}

/**
 * Connection status indicator component.
 * Isolated observer to prevent parent re-renders.
 */
const ConnectionIndicator = observer(function ConnectionIndicator(): React.ReactElement {
  const appStore = useAppStore();

  const statusConfig = {
    connecting: {
      color: 'bg-treasure-amber',
      text: 'Connecting...',
      pulse: true,
    },
    connected: {
      color: 'bg-game-stamina',
      text: 'Connected',
      pulse: false,
    },
    disconnected: {
      color: 'bg-muted-foreground',
      text: 'Disconnected',
      pulse: false,
    },
    error: {
      color: 'bg-game-health',
      text: 'Connection Error',
      pulse: false,
    },
  };

  const config = statusConfig[appStore.connectionStatus as keyof typeof statusConfig];

  return (
    <motion.div
      className="flex items-center gap-2 text-sm text-muted-foreground"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: 0.2 }}
    >
      <motion.span
        className={cn('h-2 w-2 rounded-full', config.color)}
        animate={config.pulse ? { scale: [1, 1.2, 1] } : {}}
        transition={{ duration: 1, repeat: Infinity }}
      />
      <span>{config.text}</span>
    </motion.div>
  );
});

/**
 * Authenticated user status display.
 */
const AuthenticatedStatus = observer(function AuthenticatedStatus(): React.ReactElement {
  const authStore = useAuthStore();
  const navigate = useNavigate();

  return (
    <motion.div
      className="flex flex-col items-center gap-4 w-full"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        className="flex items-center gap-2 text-sm text-game-stamina"
        variants={itemVariants}
      >
        <Cloud className="h-4 w-4" />
        <span>Signed in as {authStore.user?.displayName ?? 'User'}</span>
      </motion.div>

      <motion.div variants={itemVariants} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
        <Button size="lg" className="w-full" onClick={() => void navigate('/characters')}>
          <Sparkles className="h-4 w-4 mr-2" />
          Character Select
        </Button>
      </motion.div>

      <motion.p
        className="text-center text-xs font-medium text-biolume-cyan/80 tracking-wide"
        variants={itemVariants}
      >
        Beta access coming Spring 2026
      </motion.p>
    </motion.div>
  );
});

/**
 * Guest user status display.
 */
const GuestStatus = observer(function GuestStatus(): React.ReactElement {
  const navigate = useNavigate();

  return (
    <motion.div
      className="flex flex-col items-center gap-4 w-full"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div
        className="flex items-center gap-2 text-sm text-muted-foreground"
        variants={itemVariants}
      >
        <AlertTriangle className="h-4 w-4 text-treasure-amber" />
        <span>Playing as Guest</span>
      </motion.div>

      <motion.div variants={itemVariants} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
        <Button size="lg" className="w-full" onClick={() => void navigate('/characters')}>
          <Sparkles className="h-4 w-4 mr-2" />
          Character Select
        </Button>
      </motion.div>

      <motion.p className="text-center text-xs text-muted-foreground" variants={itemVariants}>
        Your progress is saved locally only.
      </motion.p>
      <motion.p
        className="text-center text-xs font-medium text-biolume-cyan/80 tracking-wide"
        variants={itemVariants}
      >
        Beta access coming Spring 2026
      </motion.p>
    </motion.div>
  );
});

/**
 * Loading status while checking session.
 */
function LoadingStatus(): React.ReactElement {
  return (
    <motion.div
      className="flex flex-col items-center gap-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Spinner size="md" />
      <p className="text-center text-xs text-muted-foreground">Checking session...</p>
    </motion.div>
  );
}

/**
 * Connecting status while establishing connection.
 */
function ConnectingStatus(): React.ReactElement {
  return (
    <motion.div
      className="flex flex-col items-center gap-3"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Spinner size="lg" />
      <div className="flex flex-col items-center gap-1">
        <span className="text-sm text-muted-foreground">Establishing connection...</span>
        <motion.span
          className="text-xs text-muted-foreground/50"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Preparing your adventure
        </motion.span>
      </div>
    </motion.div>
  );
}

/**
 * Login choice buttons.
 */
const LoginChoices = observer(function LoginChoices(): React.ReactElement {
  const authStore = useAuthStore();

  return (
    <motion.div
      className="flex flex-col items-center gap-4 w-full"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Error message if any */}
      <AnimatePresence>
        {authStore.error && (
          <motion.div
            className="flex items-start gap-2 text-xs text-destructive bg-destructive/10 rounded-md p-3 w-full"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <p>{authStore.error.message}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Google Sign In */}
      <motion.div
        variants={itemVariants}
        className="w-full"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <LoginButton fullWidth size="lg" variant="default" />
      </motion.div>

      {/* Divider */}
      <motion.div className="flex items-center gap-3 w-full" variants={itemVariants}>
        <div className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground uppercase tracking-wider">or</span>
        <div className="h-px flex-1 bg-border" />
      </motion.div>

      {/* Guest option */}
      <motion.div
        variants={itemVariants}
        className="w-full"
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
      >
        <Button
          variant="outline"
          size="lg"
          className="w-full"
          onClick={() => {
            void authStore.continueAsGuest();
          }}
        >
          Continue as Guest
        </Button>
      </motion.div>

      {/* Guest warning */}
      <motion.div
        className="flex items-start gap-2 text-xs text-muted-foreground bg-muted/30 rounded-md p-3 w-full"
        variants={itemVariants}
      >
        <AlertTriangle className="h-4 w-4 text-treasure-amber shrink-0 mt-0.5" />
        <p>
          <span className="font-medium text-foreground">Guest progress is saved locally only.</span>{' '}
          It won&apos;t sync across devices or browsers. You can sign in later to enable cloud
          saves.
        </p>
      </motion.div>
    </motion.div>
  );
});

/**
 * Error message display.
 */
const ErrorMessage = observer(function ErrorMessage(): React.ReactElement | null {
  const appStore = useAppStore();

  if (!appStore.errorMessage) {
    return null;
  }

  return (
    <motion.div
      className="w-full rounded-md bg-destructive/10 border border-destructive/30 p-3 text-center text-sm text-destructive"
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      {appStore.errorMessage}
    </motion.div>
  );
});

/**
 * Card content that renders based on connection and auth status.
 * This is an isolated observer to prevent the entire page from re-rendering.
 */
const StatusCardContent = observer(function StatusCardContent(): React.ReactElement {
  const appStore = useAppStore();
  const authStore = useAuthStore();

  let statusContent: React.ReactElement = <LoginChoices />;
  // Not connected yet - show connecting spinner
  if (appStore.isConnecting) {
    statusContent = <ConnectingStatus />;
  }

  // Connected - show auth-based content
  if (appStore.isConnected) {
    // Auth is still loading
    if (authStore.status === 'loading') {
      statusContent = <LoadingStatus />;
    }

    // User is authenticated
    if (authStore.status === 'authenticated') {
      statusContent = <AuthenticatedStatus />;
    }

    // User is guest
    if (authStore.status === 'guest') {
      statusContent = <GuestStatus />;
    }

    // User needs to choose login method
    return (
      <>
        <ConnectionIndicator />
        <ErrorMessage />
        {statusContent}
      </>
    );
  }

  // Connection error or disconnected
  return (
    <>
      <ConnectionIndicator />
      <ErrorMessage />
    </>
  );
});

/**
 * Version footer - isolated to prevent re-renders
 */
const VersionFooter = observer(function VersionFooter(): React.ReactElement {
  const appStore = useAppStore();

  return (
    <motion.div
      className="flex flex-col items-center gap-1 text-xs text-muted-foreground"
      style={{ textShadow: '0 2px 8px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.7)' }}
      variants={footerVariants}
      initial="hidden"
      animate="visible"
    >
      <span>v{appStore.version}</span>
      <motion.span
        className="text-treasure-gold/80"
        animate={{ opacity: [0.6, 1, 0.6] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      >
        {DEVELOPER_NAME}
      </motion.span>
    </motion.div>
  );
});

/**
 * Splash page component displayed on application load.
 *
 * Shows:
 * - Game title and subtitle with glowing effects
 * - Abyssal Beacon logo
 * - Connection status to backend
 * - Login options (Google / Guest)
 * - Version information
 *
 * The page is split into isolated observer components to prevent
 * unnecessary re-renders of the background and animations.
 */
export function SplashPage(): React.ReactElement {
  return (
    <div className="relative flex min-h-screen flex-col items-center justify-center p-4">
      {/* Background image */}
      <motion.div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: 'url(/assets/background-image-1.png)' }}
        initial={{ scale: 1.1, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 1.5, ease: 'easeOut' }}
      />
      {/* Dark overlay for readability */}
      <div className="absolute inset-0 bg-abyss-void/60" />
      {/* Bottom vignette for footer readability */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'linear-gradient(to bottom, transparent 50%, rgba(2,6,23,0.8) 100%)',
        }}
      />

      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Top glow */}
        <motion.div
          className="absolute -top-1/4 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-biolume-cyan/5 blur-3xl"
          animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.1, 1] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        />
        {/* Bottom glow */}
        <motion.div
          className="absolute -bottom-1/4 left-1/4 h-96 w-96 rounded-full bg-rarity-epic/5 blur-3xl"
          animate={{ opacity: [0.2, 0.4, 0.2] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
        {/* Right accent */}
        <motion.div
          className="absolute top-1/3 -right-20 h-64 w-64 rounded-full bg-treasure-gold/5 blur-3xl"
          animate={{ opacity: [0.1, 0.3, 0.1] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
        />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center gap-8">
        {/* Logo */}
        <AbyssalBeaconLogo />

        {/* Title with glow effect */}
        <div className="flex flex-col items-center gap-2 text-center">
          <motion.h1
            className="font-display text-5xl font-bold tracking-wide text-foreground drop-shadow-[0_0_40px_rgba(34,211,238,0.4)] drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]"
            variants={titleVariants}
            initial="hidden"
            animate="visible"
          >
            {APP_NAME}
          </motion.h1>
          <motion.p
            className="font-display text-xl tracking-[0.3em] uppercase text-biolume-cyan/70 drop-shadow-[0_0_15px_rgba(34,211,238,0.3)]"
            variants={subtitleVariants}
            initial="hidden"
            animate="visible"
          >
            {APP_SUBTITLE}
          </motion.p>
        </div>

        {/* Status card - content is isolated observer */}
        <motion.div variants={cardVariants} initial="hidden" animate="visible">
          <Card className="w-full max-w-sm">
            <CardContent className="flex flex-col items-center gap-4 p-6">
              <StatusCardContent />
            </CardContent>
          </Card>
        </motion.div>

        {/* Footer */}
        <VersionFooter />
      </div>
    </div>
  );
}

export default SplashPage;
